import React, { useState } from "react";
import { Breadcrumb } from "react-bootstrap";
import { NavLink, useHistory } from "react-router-dom";
import usersApi, { UserCreateReq } from "../../api/users";
import { Loader, toast } from "../../common/components/core";
import { useAuthErrorHandlerCreator } from "../../common/hooks";
import { useGeneralErrorHandlerCreator } from "../../common/hooks/useGeneralErrorHandler";
import { UserRole } from "../../types/userCurrent";
import { composeErrorHandlers, GenericError } from "../../utils/errorHandling";

import CreateNewUserForm, {
    CreateNewUserFormValues,
} from "./components/CreateNewUserForm";
import { UserResponsePayload } from "../../api/project";
import { PageTitle } from "../../types/pageTitle";

const CreateNewUserView = (): JSX.Element => {
    document.title = PageTitle.CreateNewUserView;
    const history = useHistory();
    const onAuthError = useAuthErrorHandlerCreator();
    const useGeneralError = useGeneralErrorHandlerCreator();
    const [isSubmitting, setSubmitting] = useState(false);

    const submit = async (values: CreateNewUserFormValues) => {
        setSubmitting(true);
        try {
            const req: UserCreateReq = {
                email: values.email,
                firstName: values.firstName,
                lastName: values.lastName,
                role: values.roleType as UserRole,
                userProfileId: +values.userProfileId,
            };
            const createUserRes: UserResponsePayload = await usersApi.create(
                req,
            );
            toast("User Created");

            history.push(
                `/user-management/user-information/${createUserRes.id}`,
            );
        } catch (e) {
            composeErrorHandlers(
                onAuthError,
                useGeneralError,
            )(e as GenericError);
        }
        setSubmitting(false);
    };

    return <PureCreateNewUserView isLoading={isSubmitting} onSubmit={submit} />;
};

type PureCreateNewUserViewProps = {
    isLoading: boolean;
    onSubmit: (values: CreateNewUserFormValues) => void;
};

export const PureCreateNewUserView = ({
    isLoading,
    onSubmit,
}: PureCreateNewUserViewProps): JSX.Element => {
    const history = useHistory();

    return (
        <Loader
            overlay={true}
            isLoading={isLoading}
            bgClassName="bg-body"
        >
            <Breadcrumb className="mb-5">
                <Breadcrumb.Item linkAs="span">
                    <NavLink to="/user-management">User Management</NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="fw-bold">
                    Create New User
                </Breadcrumb.Item>
            </Breadcrumb>
            <CreateNewUserForm
                onCancel={() => history.push("/user-management")}
                onSubmit={onSubmit}
            />
        </Loader>
    );
};

export default CreateNewUserView;
